import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import walletPagePerformanceSectionMessages from '../../../messages/wallet/wallet-page-performance-section.messages';
import {
  ProductPerformanceDateFilterType,
  RangeDate,
  TabItem,
} from '../../../model';
import {
  getSelectedWalletSection,
  myWalletSliceActions,
  retrieveProductsPerformances,
  sharedAccountsSliceActions,
} from '../../../store/features/wallet';
import { isRangeDateValid } from '../../../utils/validators';
import { useAppDispatch, useAppSelector } from '../../redux';
import {
  MyWalletPerformanceFilterLogic,
  useMyWalletPerformanceFilterLogic,
} from './my-wallet-performance-filter-logic';

export const performanceFilterDateTypesMap: Record<
  ProductPerformanceDateFilterType,
  MessageDescriptor
> = {
  START_YEAR: walletPagePerformanceSectionMessages.beginningOfTheYearLabel,
  LAST_YEAR: walletPagePerformanceSectionMessages.lastTwelveMonthsLabel,
  DA_APERTURA: walletPagePerformanceSectionMessages.openLabel
};

const getDateTabs = (
  _: ProductPerformanceDateFilterType,
  formatMessage: (descriptor: MessageDescriptor) => string
): TabItem<ProductPerformanceDateFilterType>[] => {
  return [
    {
      type: 'START_YEAR',
      label: formatMessage(
        walletPagePerformanceSectionMessages.beginningOfTheYearLabel
      ),
    },
    {
      type: 'LAST_YEAR',
      label: formatMessage(
        walletPagePerformanceSectionMessages.lastTwelveMonthsLabel
      ),
    },
    {
      type: 'DA_APERTURA',
      label: formatMessage(walletPagePerformanceSectionMessages.openLabel),
    },
  ];
};

export interface MyWalletPerformanceDateFilterLogic
  extends MyWalletPerformanceFilterLogic {
  rangeDate: RangeDate;
  enableApplyButton: boolean;
  dateTabs: TabItem<ProductPerformanceDateFilterType>[];

  onSelectedDateTab: (tab: ProductPerformanceDateFilterType) => void;
  onCalendarDateChanged: (range: RangeDate) => void;
  onApplyClick: () => void;
}

export const useMyWalletPerformanceDateFilterLogic =
  (): MyWalletPerformanceDateFilterLogic => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const {
      flagOpenDateFilter,
      performanceFilter,
      onPerformanceFilterChange,
      onToggleDateFilter,
    } = useMyWalletPerformanceFilterLogic();

    const [rangeDate, setRangeDate] = useState<RangeDate>();
    const enableApplyButton = useMemo(
      () => isRangeDateValid(rangeDate),
      [rangeDate]
    );

    const selectedWalletSection = useAppSelector(getSelectedWalletSection);

    const actions = useMemo(
      () =>
        selectedWalletSection === 'MY_WALLET'
          ? myWalletSliceActions
          : sharedAccountsSliceActions,
      [selectedWalletSection]
    );

    useEffect(() => {
      setRangeDate(performanceFilter.rangeDate);
    }, [performanceFilter, setRangeDate]);

    const onCalendarDateChanged = useCallback(
      (range: RangeDate) => setRangeDate(range),
      [setRangeDate]
    );

    const dateTabs = useMemo(
      () => getDateTabs(performanceFilter.dateType, formatMessage),
      [performanceFilter, formatMessage]
    );

    const onSelectedDateTab = useCallback(
      (tab: ProductPerformanceDateFilterType) => {
        dispatch(
          actions.setPerformanceFilter({
            ...performanceFilter,
            rangeDate: undefined,
            dateType: tab,
          })
        );
          onToggleDateFilter(false);
          dispatch(retrieveProductsPerformances());
      },
      [dispatch, onToggleDateFilter, performanceFilter, actions]
    );

    const onApplyClick = useCallback(() => {
      onPerformanceFilterChange({ ...performanceFilter, rangeDate });
      onToggleDateFilter();
    }, [
      onPerformanceFilterChange,
      onToggleDateFilter,
      performanceFilter,
      rangeDate,
    ]);

    return {
      dateTabs,
      flagOpenDateFilter,
      performanceFilter,
      enableApplyButton,
      rangeDate,
      onSelectedDateTab,
      onPerformanceFilterChange,
      onToggleDateFilter,
      onApplyClick,
      onCalendarDateChanged,
    };
  };
